import axios from 'axios';

// Types for the settings API
export interface ModelConfig {
  model_id: string;
  display_name: string;
  provider: string;
  region: string;
  api_version: string | null;
  description: string;
}

export interface AvailableModelsResponse {
  models: ModelConfig[];
  grouped: {
    [provider: string]: ModelConfig[];
  };
  default_model: string;
  environment: string;
}

export interface CurrentModelResponse {
  model_id: string;
  display_name: string;
  provider: string;
  description: string;
  environment: string;
}

export interface SelectModelResponse {
  message: string;
  model_id: string;
  display_name: string;
  provider: string;
  description: string;
}

// Helper function for axios requests with auth handling
async function axiosRequest<T>(axiosPromise: Promise<T>): Promise<T> {
  try {
    return await axiosPromise;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      // Do NOT redirect on 401 here. PermissionsContext + RequireAuth handle
      // unauthenticated state globally without causing a full page reload loop.
      throw error;
    }
    throw error;
  }
}

/**
 * Get all available LLM models
 */
export async function getAvailableModels(): Promise<AvailableModelsResponse> {
  try {
    const response = await axiosRequest(
      axios.get('/api/settings/available-models', {
        withCredentials: true,
      })
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

/**
 * Get the currently selected model for the user
 */
export async function getCurrentModel(): Promise<CurrentModelResponse> {
  try {
    const response = await axiosRequest(
      axios.get('/api/settings/current-model', {
        withCredentials: true,
      })
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

/**
 * Set the selected model for the user
 */
export async function selectModel(modelId: string): Promise<SelectModelResponse> {
  try {
    const response = await axiosRequest(
      axios.post(
        '/api/settings/select-model',
        { model_id: modelId },
        { withCredentials: true }
      )
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

/**
 * Get environment information
 */
export async function getEnvironmentInfo(): Promise<{
  environment: string;
  description: { [key: string]: string };
}> {
  try {
    const response = await axiosRequest(
      axios.get('/api/settings/environment', {
        withCredentials: true,
      })
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}
